////////////////////////////////////////////
// CONNECT BUTTON — THE NAV'S WALLET SURFACE (CHAIN-AGNOSTIC)
////////////////////////////////////////////
//
// A VIEW OVER useConnect().active — THE CONNECTED WALLET FROM EITHER CHAIN:
//   · DISCONNECTED → "Connect wallet" OPENS THE ONE SHARED MODAL
//   · CONNECTED → THE PILL OPENS THE ACCOUNT MENU. THE APP NEVER VALIDATES
//     THE WALLET AGAINST THE PAGE/TOKEN — WHATEVER IS CONNECTED IS USED, AND
//     THE WALLET APP ITSELF REPORTS ANYTHING IT CAN'T SIGN.
//
// THE MENU SHOWS THE WALLET APP IN USE (BRAND ICON + NAME), THE ACCOUNT'S
// CHAIN (NATIVE CHAIN ICON), AND ITS NET AS A READ-ONLY PILL — NETS ARE
// SWITCHED BY CONNECTING A WALLET ON THAT NET, NEVER FROM HERE.
// A "Connecting…" STATE THAT NEVER RESOLVES (E.G. A STALE WALLETCONNECT
// RESTORE) FALLS BACK TO "Connect wallet" AFTER A FEW SECONDS.
// MOBILE-FIRST: A COMPACT CHIP IN THE TOP BAR EVERYWHERE; THE ACCOUNT MENU
// IS A BOTTOM SHEET (SCRIM + SAFE-AREA, THUMB REACH) ON MOBILE AND AN
// ANCHORED DROPDOWN FROM lg UP.
import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { useConnect, type ActiveWallet } from "@/app/providers/ConnectProvider";
import { useRole } from "@/app/providers/AuthProvider";
import { RH_EXPLORER_MAINNET, RH_EXPLORER_TESTNET } from "@/config/protocol";
import { useWalletBalance } from "@/lib/wallet/useWalletBalance";
import { useDismiss } from "@/lib/useDismiss";
import { MenuItem, MenuPanel } from "@/components/ui/Menu";

const BTN =
  "bg-ember hover:bg-blaze text-[#1A0E06] font-bold text-[12px] lg:text-[13px] tracking-[.5px] uppercase " +
  "px-[14px] py-[9px] lg:px-[18px] lg:py-[11px] rounded-[10px] border-0 cursor-pointer whitespace-nowrap";

const PILL =
  "inline-flex items-center gap-[8px] font-mono text-[11.5px] lg:text-[12px] tracking-[.5px] " +
  "px-[11px] py-[8px] lg:px-[14px] lg:py-[10px] rounded-[10px] " +
  "border cursor-pointer whitespace-nowrap transition-colors";

// READ-ONLY NET TAG — INFORMATIONAL, NEVER A CONTROL
const NET_PILL =
  "inline-flex items-center px-[8px] py-[3px] rounded-full border font-mono text-[10px] uppercase tracking-[.6px]";

// A STUCK "Connecting…" (STALE WALLETCONNECT RESTORE) FALLS BACK AFTER THIS.
// THE VERDICT PERSISTS IN sessionStorage — THE NAV (AND THIS BUTTON) CAN
// REMOUNT ON ROUTE CHANGES, AND COMPONENT-LOCAL STATE WOULD RESET THE GRACE
// WINDOW EVERY NAVIGATION, FLASHING "Connecting…" ON EVERY PAGE FOREVER.
const CONNECTING_GRACE_MS = 8000;
const PENDING_STALE_KEY = "fuze:wallet-pending-stale";

function readStale(): boolean {

  try { return sessionStorage.getItem(PENDING_STALE_KEY) === "1"; } catch { return false; }
}

function writeStale(on: boolean) {

  try {

    if (on) sessionStorage.setItem(PENDING_STALE_KEY, "1");
    else sessionStorage.removeItem(PENDING_STALE_KEY);
  } catch { /* STORAGE BLOCKED — GRACE JUST RESETS PER MOUNT */ }
}

export function ConnectButton() {

  const c = useConnect();
  const { alias } = useRole();
  const { connector } = useAccount();
  // BALANCE READS GO THROUGH THE PAGE'S OWN RPC/API — ONLY MEANINGFUL WHEN
  // THE WALLET LIVES ON THE PAGE'S CHAIN (ADDRESS SHAPES DIFFER OTHERWISE)
  const balance = useWalletBalance(c.active && c.active.chain === c.chain ? c.active.address : null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useDismiss<HTMLSpanElement>(menuOpen, () => setMenuOpen(false));

  // MOBILE SHEET — LOCK BACKGROUND SCROLL WHILE IT'S OPEN
  useEffect(() => {

    if (!menuOpen || window.matchMedia("(min-width: 1024px)").matches)
      return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  // CONNECTING — QUIET PENDING STATE (COVERS wagmi 'reconnecting' ON RELOAD),
  // BUT NEVER FOREVER: AN ABANDONED WALLETCONNECT PAIRING CAN LEAVE WAGMI
  // SITTING IN 'connecting' ACROSS RELOADS, AND THE USER STILL NEEDS A BUTTON.
  // ONCE JUDGED STALE, THE VERDICT STICKS FOR THE BROWSER SESSION.
  const pending = !c.active && (c.status === "connecting" || c.status === "reconnecting");
  const [pendingStale, setPendingStale] = useState(readStale);
  useEffect(() => {

    if (!pending || pendingStale)
      return;
    const t = setTimeout(() => {

      setPendingStale(true);
      writeStale(true);
    }, CONNECTING_GRACE_MS);
    return () => clearTimeout(t);
  }, [pending, pendingStale]);

  // A REAL CONNECTION LANDING CLEARS THE VERDICT — THE NEXT RELOAD'S GENUINE
  // SESSION RESTORE GETS ITS QUIET "Connecting…" BACK
  useEffect(() => {

    if (c.active) {

      setPendingStale(false);
      writeStale(false);
    }
  }, [c.active]);

  if (pending && !pendingStale)
    return (
      <button className={BTN + " opacity-70"} disabled>Connecting…</button>
    );

  // NOTHING CONNECTED ANYWHERE — OPEN THE ONE SHARED MODAL
  if (!c.active)
    return (
      <button className={BTN} onClick={c.openConnect}>Connect wallet</button>
    );

  const a = c.active;

  // EXPLORER FOR THE ACCOUNT'S OWN CHAIN/NET — NOT THE PAGE'S
  const explorerHref = a.chain === "rh"
    ? (a.chainName === "Ethereum"
        ? "https://etherscan.io/address/" + a.address
        : (a.netLabel === "testnet" ? RH_EXPLORER_TESTNET : RH_EXPLORER_MAINNET) + "/address/" + a.address)
    : "https://solscan.io/account/" + a.address + (a.netLabel === "devnet" ? "?cluster=devnet" : "");

  async function copy() {

    try {
      await navigator.clipboard.writeText(a.address || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* CLIPBOARD BLOCKED — NON-FATAL */ }
  }

  // EVM ACCOUNT SWITCH — wallet_requestPermissions RE-OPENS THE WALLET'S
  // ACCOUNT PICKER (METAMASK ET AL); FAILURES/DISMISSALS ARE NON-FATAL
  async function switchAccount() {

    try {
      const provider = (await connector?.getProvider?.()) as
        { request?: (a: { method: string; params?: unknown[] }) => Promise<unknown> } | undefined;
      await provider?.request?.({ method: "wallet_requestPermissions", params: [{ eth_requestAccounts: {} }] });
    } catch { /* DISMISSED — KEEP THE CURRENT ACCOUNT */ }
    setMenuOpen(false);
  }

  // CHANGE WALLET — DROP THE SESSION, REOPEN THE SHARED PICKER
  function changeWallet() {

    setMenuOpen(false);
    c.disconnect();
    c.openConnect();
  }

  return (
    <span className="relative inline-flex" ref={wrapRef}>
      <button
        className={PILL + " text-fg border-line hover:border-ember"}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={a.app.name + " · " + a.chainName + " · " + a.netLabel}
        onClick={() => setMenuOpen((o) => !o)}
      >
        <AppMark app={a.app} size={15} />
        {a.short}
        <span className="w-[7px] h-[7px] rounded-full flex-none bg-green" aria-label="connected" />
      </button>
      {menuOpen && (
        <>
          {/* MOBILE SCRIM — TAP DISMISSES; DESKTOP DISMISSES VIA OUTSIDE CLICK */}
          <div
            className="fixed inset-0 z-50 bg-black/55 lg:hidden"
            aria-hidden="true"
            onClick={() => setMenuOpen(false)}
          />
          <MenuPanel placement="sheet" className="lg:min-w-[335px]">
          {/* WALLET APP — THE APP HOLDING THE KEYS, WITH ITS BRAND MARK */}
          <div className="flex items-center gap-[9px] px-[10px] pt-[8px] pb-[9px]">
            <AppMark app={a.app} size={26} rounded />
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-fg leading-tight">{a.app.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-[.5px] text-dim mt-[1px]">wallet app</div>
            </div>
            {balance && <span className="ml-auto font-mono text-[12px] text-fg">{balance}</span>}
          </div>

          {/* ACCOUNT — THE CHAIN IT'S ACTUALLY ON + READ-ONLY NET PILL + FULL ADDRESS */}
          <div className="px-[10px] pb-[10px] border-b border-line mb-[6px]">
            <div className="flex items-center gap-[7px] text-[12.5px] font-semibold text-fg">
              <img className="w-[15px] h-[15px] object-contain rounded-[3px]" src={a.chainIcon} alt="" />
              {a.chainName}
              <span className={NET_PILL + " ml-auto border-line bg-white/4 text-dim"}>{a.netLabel}</span>
            </div>
            <div className="font-mono text-[11px] text-dim whitespace-nowrap overflow-hidden text-ellipsis mt-[7px] leading-[1.5]">{a.address}</div>
            {alias && (
              <div className="text-[11.5px] text-dim mt-[6px]">signed in as <b className="text-fg">{alias}</b></div>
            )}
          </div>

          {/* ACCOUNT ACTIONS — SOLANA HAS NO IN-PAGE ACCOUNT SWITCH (THE
              WALLET APP SWITCHES AND THE ADAPTER FOLLOWS LIVE) */}
          {a.chain === "rh" && (
            <MenuItem className="text-[13px] text-fg" onClick={switchAccount}>
              Switch account
            </MenuItem>
          )}
          <MenuItem className="text-[13px] text-fg" onClick={changeWallet}>
            Change wallet
          </MenuItem>
          <MenuItem className="text-[13px] text-fg" onClick={copy}>
            {copied ? "Copied ✓" : "Copy address"}
          </MenuItem>
          <MenuItem className="text-[13px] text-fg" onClick={() => window.open(explorerHref, "_blank", "noopener")}>
            View on explorer ↗
          </MenuItem>
          <MenuItem className="text-[13px] text-red" onClick={() => { setMenuOpen(false); c.disconnect(); }}>
            Disconnect
          </MenuItem>
          </MenuPanel>
        </>
      )}
    </span>
  );
}

// WALLET-APP MARK — BRAND ICON, OR A BRAND-COLORED DOT WHEN NO ICON EXISTS
function AppMark({ app, size, rounded }: { app: ActiveWallet["app"]; size: number; rounded?: boolean }) {

  if (app.icon)
    return (
      <img
        className={"flex-none object-cover bg-white/8 " + (rounded ? "rounded-[7px]" : "rounded-[4px]")}
        style={{ width: size, height: size }}
        src={app.icon}
        alt={app.name}
      />
    );
  return (
    <span
      className="rounded-full flex-none"
      style={{ width: Math.round(size * 0.6), height: Math.round(size * 0.6), background: app.color || "#888" }}
    />
  );
}
